import type { OnInit } from '@angular/core';
import { Component, input, inject, signal } from '@angular/core';
import { ImageCropperComponent, type ImageCroppedEvent } from 'ngx-image-cropper';
import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';
import { MatButton, MatIconButton } from '@angular/material/button';
import { isNil } from '@pct-ts/typeguard';
import { MatDialogRef } from '@angular/material/dialog';
import { type CropConfig, aspectRatioToNum } from '@pct-ng/feature-file-upload-native';
import { CustomMatSvgIconDirective } from '@pct-ng/icon';
import { MatIcon } from '@angular/material/icon';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonToggleGroup, MatButtonToggle } from '@angular/material/button-toggle';

@Component({
  selector: 'pct-feature-file-upload-material-cappus-image-crop-dialog',
  imports: [
    ImageCropperComponent,
    MatButton,
    TranslocoDirective,
    CustomMatSvgIconDirective,
    MatIcon,
    MatIconButton,
    MatSlideToggle,
    ReactiveFormsModule,
    MatButtonToggleGroup,
    MatButtonToggle,
    TranslocoPipe,
  ],
  templateUrl: './image-crop-dialog.component.html',
})
export class ImageCropDialogComponent implements OnInit {
  dialogRef = inject(MatDialogRef<ImageCropDialogComponent>);

  protected readonly aspectRatioOptions: { displayName: string; value: number }[] = [
    {
      displayName: '4/3',
      value: 4 / 3,
    },
    {
      displayName: '16/9',
      value: 16 / 9,
    },
  ];

  protected readonly cropConfig = input.required<CropConfig>();

  protected canvasRotation = signal(0);

  protected readonly maintainAspectRatioControl = new FormControl(false, {
    nonNullable: true,
  });
  protected readonly containWithinAspectRatioControl = new FormControl(false, {
    nonNullable: true,
  });

  protected readonly aspectRatioControl = new FormControl(4 / 3, {
    nonNullable: true,
  });

  selectedFile = input.required<File>();

  protected croppedBlob?: Blob;

  ngOnInit(): void {
    this.maintainAspectRatioControl.setValue(this.cropConfig().maintainAspectRatioDefaultValue);
    this.containWithinAspectRatioControl.setValue(
      this.cropConfig().containWithinAspectRatioDefaultValue,
    );
    this.aspectRatioControl.setValue(aspectRatioToNum(this.cropConfig().defaultAspectRatio));
  }

  protected onImageCropped(event: ImageCroppedEvent): void {
    if (!isNil(event.blob)) {
      this.croppedBlob = event.blob;
    }
  }

  protected confirmCrop(): void {
    if (isNil(this.croppedBlob) || isNil(this.selectedFile)) {
      return;
    }

    const croppedFile = new File([this.croppedBlob], this.selectedFile().name, {
      type: this.selectedFile().type,
    });

    this.dialogRef.close(croppedFile);
  }

  protected cancelCrop(): void {
    this.dialogRef.close(null);
  }

  protected rotateCanvas(direction: 'left' | 'right'): void {
    if (direction === 'left') {
      this.canvasRotation.update(prev => prev - 1);
    } else {
      this.canvasRotation.update(prev => prev + 1);
    }
  }
}
